package com.nonopn.skywatcher.design.widgets.abslists

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyGridItemScope
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.pulltorefresh.PullToRefreshBox
import androidx.compose.material3.pulltorefresh.rememberPullToRefreshState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import com.nonopn.skywatcher.design.widgets.defaultBackground
import eu.codlab.platform.Platform
import eu.codlab.platform.currentPlatform

@OptIn(ExperimentalFoundationApi::class, ExperimentalMaterial3Api::class)
@Composable
fun <
        T : AbstractSectionListModel<M, H, E>,
        M : AbstractSectionListModelState<H, E>,
        H,
        E
        > AbstractSectionLists(
    model: T,
    columns: Int = rememberColumns(),
    textWhenEmptyList: String? = null,
    header: (@Composable () -> Unit)? = null,
    renderSectionKey: (H) -> String,
    renderElementKey: (H, E, Int) -> String,
    renderSectionTitle: @Composable LazyGridItemScope.(H) -> Unit,
    renderElement: @Composable LazyGridItemScope.(Int, H, E) -> Unit
) {
    val pullToRefreshState = rememberPullToRefreshState()
    val state by model.states.collectAsState()
    val modelState = state.modelState

    val modifier = Modifier
        .fillMaxSize()
        .defaultBackground()

    Column(
        modifier = modifier
    ) {
        header?.invoke()
        Box(
            modifier = Modifier.fillMaxSize(),
        ) {
            // add pull to refresh on iOS & Android only
            if (listOf(Platform.IOS, Platform.ANDROID).contains(currentPlatform)) {
                PullToRefreshBox(
                    isRefreshing = listOf(
                        ListModelState.DEFAULT,
                        ListModelState.LOADING
                    ).contains(modelState.state),
                    modifier = Modifier.fillMaxSize(),
                    state = pullToRefreshState,
                    onRefresh = {
                        model.load()
                    }
                ) {
                    ShowSectionLoaded(
                        Modifier.fillMaxSize(),
                        state,
                        columns,
                        renderSectionKey,
                        renderElementKey,
                        renderSectionTitle,
                        renderElement
                    )
                }
            } else {
                ShowSectionLoaded(
                    Modifier.fillMaxSize(),
                    state,
                    columns,
                    renderSectionKey,
                    renderElementKey,
                    renderSectionTitle,
                    renderElement
                )
            }

            when (modelState.state) {
                ListModelState.ERROR -> ShowError(modifier)
                ListModelState.DEFAULT -> ShowLoading(Modifier.fillMaxSize())
                ListModelState.LOADING -> ShowLoading(Modifier.fillMaxSize())
                ListModelState.LOADED -> {
                    if (null != textWhenEmptyList && state.modelState.objects.isEmpty()) {
                        // ShowEmpty(text = textWhenEmptyList)
                    }
                }
            }
        }
    }
}

@Suppress("MagicNumber")
@Composable
fun <H, E> ShowSectionLoaded(
    modifier: Modifier,
    state: AbstractSectionListModelState<H, E>,
    columns: Int = rememberColumns(),
    renderSectionKey: (H) -> String,
    renderElementKey: (H, E, Int) -> String,
    renderSectionTitle: @Composable LazyGridItemScope.(H) -> Unit,
    renderElement: @Composable LazyGridItemScope.(Int, H, E) -> Unit
) {
    val filtered = state.modelState.filtered

    LazyVerticalGrid(
        modifier = modifier,
        state = rememberLazyGridState(),
        columns = GridCells.Fixed(columns)
    ) {
        filtered.forEach { section ->
            val entities = section.entities

            item(
                key = renderSectionKey(section.header),
                span = { GridItemSpan(columns) }
            ) { renderSectionTitle(section.header) }

            items(
                entities.size,
                key = { index -> renderElementKey(section.header, entities[index], index) }
            ) {
                renderElement(0, section.header, entities[it])
            }
        }
    }
}
